import React, { useEffect, useMemo, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Card, CardBody } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../hook/request';
import { Btn } from '../../../AbstractElements';

type RawUser = {
    ID: number;
    nome: string;
    cognome: string;
    email: string;
    data?: string | null;
    typeID?: string | number;
};

type Group = { ID: number; nome: string };

function fmtDate(iso?: string | null) {
    if (!iso) return '-';
    const s = iso.trim();
    if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return iso;
    const [y, m, d] = s.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
}

export default function UsersList() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<RawUser[]>([]);
    const [typeMap, setTypeMap] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(false);
    const [loadingTypes, setLoadingTypes] = useState(false);

    async function loadUsers() {
        setLoading(true);
        try {
            const res = await get<any>('/users/list');
            const payload = (res as any)?.data ?? res;
            const list: RawUser[] = Array.isArray(payload) ? payload : [];
            setRows(list);
        } finally {
            setLoading(false);
        }
    }

    async function loadTypes() {
        setLoadingTypes(true);
        try {
            const res = await get<any>('permissions/group?type=all');
            const payload = (res as any)?.data ?? res;
            const groups: Group[] = Array.isArray(payload) ? payload : [];
            const map: Record<number, string> = {};
            groups.forEach(g => { map[g.ID] = g.nome; });
            setTypeMap(map);
        } finally {
            setLoadingTypes(false);
        }
    }

    useEffect(() => {
        loadUsers();
        loadTypes();
    }, []);

    const columns: TableColumn<RawUser>[] = useMemo(
        () => [
            {
                name: 'Nome',
                selector: r => r.nome ?? '',
                sortable: true,
                grow: 1.2,
            },
            {
                name: 'Cognome',
                selector: r => r.cognome ?? '',
                sortable: true,
                grow: 1.2,
            },
            {
                name: 'Email',
                selector: r => r.email ?? '',
                sortable: true,
                grow: 1.6,
                cell: r => r.email || '-',
            },
            {
                name: 'Tipologia',
                selector: r => {
                    const id = Number(r.typeID ?? NaN);
                    return typeMap[id] ?? '';
                },
                sortable: true,
                grow: 1.2,
                cell: r => {
                    const id = Number(r.typeID ?? NaN);
                    return typeMap[id] || '-';
                },
            },
            {
                name: 'Data inserimento',
                selector: r => r.data ?? '',
                sortable: true,
                right: true,
                width: '160px',
                cell: r => fmtDate(r.data),
            },
            {
                name: 'Azioni',
                width: '140px',
                button: true,
                ignoreRowClick: true,
                cell: (r) => (
                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/users/${encodeURIComponent(String(r.ID))}`);
                        }}
                        title="Modifica"
                    >
                        Modifica
                    </button>
                ),
            },
        ],
        [navigate, typeMap]
    );

    return (
        <div className="page-body pt-4">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Utenti</h5>
                        <Btn
                            className="px-4" color="primary" type="button"
                            onClick={() => navigate('/admin/users/new')}
                        >
                            NUOVO
                        </Btn>
                    </div>

                    <div className="table-responsive">
                        <DataTable
                            columns={columns}
                            data={rows}
                            progressPending={loading || loadingTypes}
                            striped
                            pagination
                            highlightOnHover
                            dense
                            noDataComponent="Nessun utente disponibile"
                            paginationPerPage={50}
                            customStyles={{
                                rows: {
                                    style: {
                                        minHeight: '30px',
                                        paddingTop: '5px',
                                        paddingBottom: '5px',
                                    },
                                },
                                cells: {
                                    style: {
                                        padding: '4px 10px',
                                    },
                                },
                            }}
                        />
                    </div>
                </CardBody>
            </Card>

        </div>

    );
}
